import { Injectable, OnDestroy } from '@angular/core';
import { AssetManager } from '@app/asset-manager/asset-manager';
import { CurrentAsset } from '@app/shared/models/current-asset';
import { AssetService } from '@app/shared/services/asset.service';
import { MenuService } from '@app/template/menu/menu.service';
import { TemplateService } from '@app/template/template.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import * as THREE from '@phdsoft/three';
import { Label2DInfo, createSimpleGeometryCenterLabels2DObjects, disposeGroupLinesAndLabels } from '../graphics/labels-utils';
import { MenuHomeConfig } from '@app/template/menu/menu-data/menu-home-config';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PhdsoftCommonService, SESSION_STORAGE, StorageService, AssetInfoModel } from '@phdsoft/common';
import { SubmodelDataViewModel } from '@app/admin/pages/fea-dashboard/models/workflow.models';

export interface SubmodelLocation {
  AssetId: string;
  Name: string;
  Centroid: THREE.Vector3;
  AABB: THREE.Box3;
}

interface BoundingBoxData {
  min: [number, number, number];
  max: [number, number, number];
}

interface GetAllowedSubmodelsInBoundingBoxRequest {
  AssetId: string;
  ClientId: string;
  RegionBoundingBox: BoundingBoxData;
  AllowedAssetIds?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class SubmodelVisualizationService implements OnDestroy {
  private submodelsBoxGroup: THREE.Group;
  private submodelsMarkersGroup: THREE.Group;
  private labelInfos: Label2DInfo[] = [];
  private subscriptions: Subscription = new Subscription();

  private submodelLocations$ = new BehaviorSubject<SubmodelLocation[]>([]);
  public submodelLocationsObs$ = this.submodelLocations$.asObservable();

  private _selectedSubmodelId$ = new BehaviorSubject<string | null>(null);
  public selectedSubmodelId$ = this._selectedSubmodelId$.asObservable();

  private isModeActive: boolean = false;

  public get isSubmodel(): boolean {
    return this.assetManager.asset?.assetInfo?.IsSubmodel === true;
  }

  public get isFeaModel(): boolean {
    const assetInfo = this.assetManager.asset?.assetInfo;
    return Boolean(assetInfo?.LinkedAsset) && assetInfo?.IsCad === false;
  }

  private _hasSubmodels$ = new BehaviorSubject<boolean>(false);
  public hasSubmodels$ = this._hasSubmodels$.asObservable();

  public get hasSubmodels(): boolean {
    return this._hasSubmodels$.value;
  }

  constructor(
    private assetManager: AssetManager,
    private injector: Injector,
    private assetService: AssetService,
    private templateService: TemplateService,
    private http: HttpClient,
    private phdsoftCommonService: PhdsoftCommonService,
    private storageService: StorageService
  ) {
    this.submodelsBoxGroup = new THREE.Group();
    this.submodelsBoxGroup.name = 'SubmodelBoundingBoxes';

    this.submodelsMarkersGroup = new THREE.Group();
    this.submodelsMarkersGroup.name = 'SubmodelMarkers';

    // Delay MenuService access to break circular dependency
    setTimeout(() => {
      // was: const menuService = this.injector.get(MenuService);
      // MenuService is not provided in every injector, so the submodel navigation
      // sync is simply off there (a bare get() threw NullInjectorError).
      const menuService = this.injector.get(MenuService, null);
      if (!menuService) return;
      this.subscriptions.add(
        menuService.visualizationMode$.subscribe(mode => {
          if (mode === 'SubmodelNavigation') {
            this.activateSubmodelNavigationView();
          } else {
            this.deactivateSubmodelNavigationView();
          }
        })
      );
    });

    this.subscriptions.add(
      this.assetManager.onAssetLoaded.subscribe(async (asset) => {
        if (this.isModeActive) {
          const currentAsset = await this.assetService.getCurrentAsset();
          if (currentAsset) {
            this.clearGraphics();
            await this.discoverAndShowSubmodels(currentAsset);
          }
        }
      })
    );

    // Check submodel availability on load using the bounding box endpoint
    this.assetService.getCurrentAsset().then(async currentAsset => {
      if (!currentAsset || currentAsset.AssetInfo?.IsSubmodel) return;

      try {
        const allowedAssetIds = await this.getAllowedAssetIds();
        const regionBoundingBoxes = this.getFullSceneBoundingBoxes();
        const submodels = await this.fetchSubmodelsInBoundingBoxes(currentAsset.AssetId, currentAsset.AssetInfo.CustomerId, regionBoundingBoxes, allowedAssetIds);
        this._hasSubmodels$.next(submodels.length > 0);
      } catch {
        this._hasSubmodels$.next(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.deactivateSubmodelNavigationView();
  }

  public async activateSubmodelNavigationView() {
    if (this.isModeActive) return;
    this.isModeActive = true;
    this.assetManager.functionalityMode = 'SUBMODEL_NAVIGATION';

    const currentAsset = await this.assetService.getCurrentAsset();
    if (!currentAsset) return;

    // TODO: remove "as unknown as any" when prod migrates to new THREE
    if (!this.assetManager.graphics.scene.children.includes(this.submodelsMarkersGroup as unknown as any)) {
      this.assetManager.graphics.scene.add(this.submodelsMarkersGroup as unknown as any);
    }
    if (!this.assetManager.graphics.scene.children.includes(this.submodelsBoxGroup as unknown as any)) {
      this.assetManager.graphics.scene.add(this.submodelsBoxGroup as unknown as any);
    }

    await this.discoverAndShowSubmodels(currentAsset);
  }

  public deactivateSubmodelNavigationView() {
    if (!this.isModeActive) return;
    this.isModeActive = false;

    this._selectedSubmodelId$.next(null);
    this.submodelLocations$.next([]);

    if (this.assetManager.functionalityMode === 'SUBMODEL_NAVIGATION') {
      this.assetManager.setFunctionalityModeNone();
    }

    this.clearGraphics();
    this.templateService.toggleShowAssetInfo(false);
    this.clearSelectedLabelStyle();
  }

  private clearSelectedLabelStyle() {
    this.labelInfos.forEach(info => {
      const el = info.object2d.element as HTMLElement;
      el.classList.remove('selected');
    });
  }

  public selectSubmodel(assetId: string) {
    if (this._selectedSubmodelId$.value === assetId) {
      this.deselectSubmodel();
      return;
    }

    this.clearSelectedLabelStyle();

    this._selectedSubmodelId$.next(assetId);
    this.highlightBoundingBox(assetId);

    const index = this.submodelLocations$.value.findIndex(l => l.AssetId === assetId);
    if (index >= 0 && this.labelInfos[index]) {
      const el = this.labelInfos[index].object2d.element as HTMLElement;
      el.classList.add('selected');
    }

    this.templateService.toggleShowAssetInfo(true);
  }

  public deselectSubmodel() {
    this._selectedSubmodelId$.next(null);
    this.clearBoundingBoxes();
    this.clearSelectedLabelStyle();
    this.templateService.toggleShowAssetInfo(false);
  }

private async discoverAndShowSubmodels(currentAsset: CurrentAsset) {
  try {
    const regionBoundingBoxes = this.getFullSceneBoundingBoxes();

    const allowedAssetIds = await this.getAllowedAssetIds();
    const submodels = await this.fetchSubmodelsInBoundingBoxes(
      currentAsset.AssetId,
      currentAsset.AssetInfo.CustomerId,
      regionBoundingBoxes,
      allowedAssetIds
    );

    if (!submodels?.length) {
      this.submodelLocations$.next([]);
      return;
    }

    const submodelLocations: SubmodelLocation[] = await Promise.all(
      submodels
        .filter(s => s.BoundingBoxData != null)
        .map(async (s): Promise<SubmodelLocation> => {
          const { min, max } = s.BoundingBoxData;

          const aabb = new THREE.Box3(
            new THREE.Vector3(...min),
            new THREE.Vector3(...max)
          );

          const centroid = new THREE.Vector3();
          aabb.getCenter(centroid);

          const submodelAssetInfo = await this.assetService.getAssetInfobyId(s.Id);

          return {
            AssetId: submodelAssetInfo.AssetId,
            Name: submodelAssetInfo?.AssetInfo?.Label ?? 'Unnamed Submodel',
            Centroid: centroid,
            AABB: aabb
          };
        })
    );

    this.submodelLocations$.next(submodelLocations);
    this.renderMarkers();

  } catch (error) {
    console.warn('Failed to fetch submodels in bounding box.', error);
    this.submodelLocations$.next([]);
  }
}

  // Fetches submodels whose centroids fall within the given bounding box for the specified asset.
  private async fetchSubmodelsInBoundingBox(assetId: string, clientId: string, regionBoundingBox: BoundingBoxData, allowedAssetIds: string[]): Promise<SubmodelDataViewModel[]> {
    const url = `${environment.API_FEA}SubmodelData/GetAllowedSubmodelsInBoundingBox`;
    const body: GetAllowedSubmodelsInBoundingBoxRequest = { 
      AssetId: assetId, 
      ClientId: clientId, 
      RegionBoundingBox: regionBoundingBox,
      AllowedAssetIds: allowedAssetIds
    };
    return firstValueFrom(this.http.post<SubmodelDataViewModel[]>(url, body));
  }

  private async fetchSubmodelsInBoundingBoxes(assetId: string, clientId: string, regionBoundingBoxes: BoundingBoxData[], allowedAssetIds: string[]): Promise<SubmodelDataViewModel[]> {
    if (!regionBoundingBoxes.length) {
      return [];
    }

    const submodelBatches = await Promise.all(
      regionBoundingBoxes.map(boundingBox => this.fetchSubmodelsInBoundingBox(assetId, clientId, boundingBox, allowedAssetIds))
    );

    const dedupedSubmodels = new Map<string, SubmodelDataViewModel>();
    for (const batch of submodelBatches) {
      for (const submodel of batch) {
        if (!submodel?.Id) {
          continue;
        }
        if (!dedupedSubmodels.has(submodel.Id)) {
          dedupedSubmodels.set(submodel.Id, submodel);
        }
      }
    }

    return Array.from(dedupedSubmodels.values());
  }

  private async getAllowedAssetIds(): Promise<string[]> {
    try {
      const allAssets = await this.storageService.getItem<AssetInfoModel[]>(SESSION_STORAGE.USER_ASSETS, true);
      return (allAssets || []).map(a => a.AssetId);
    } catch (error) {
      console.error('Failed to get allowed asset IDs from storage', error);
      return [];
    }
  }

  // Computes one bounding box per visible surface in the active region(s), in world coordinates.
  private getFullSceneBoundingBoxes(): BoundingBoxData[] {
    const regions = this.assetManager.asset?.GetStructureVisibleRegions?.() ?? [];
    if (!regions.length) return [];

    const boundingBoxes: BoundingBoxData[] = [];
    const vertex = new THREE.Vector3();

    for (const region of regions) {
      const regionBox = new THREE.Box3();

      for (const section of region.sections ?? []) {
        for (const surfaceElement of section.surfaceElements ?? []) {
          // TODO: remove 'as unknown' cast when production migrates to new THREE version
          const mesh = surfaceElement?.SurfaceDesign as unknown as THREE.Mesh;
          const geometry = mesh?.geometry as THREE.BufferGeometry;
          const position = geometry?.getAttribute('position') as THREE.BufferAttribute;

          if (!mesh || !geometry || !position || position.count === 0) continue;

          mesh.updateWorldMatrix(true, false);

          for (let i = 0; i < position.count; i++) {
            vertex.fromBufferAttribute(position, i);
            mesh.localToWorld(vertex);
            regionBox.expandByPoint(vertex);
          }
        }
      }

      if (regionBox.isEmpty()) continue;

      boundingBoxes.push({
        min: [regionBox.min.x, regionBox.min.y, regionBox.min.z],
        max: [regionBox.max.x, regionBox.max.y, regionBox.max.z]
      });
    }

    return boundingBoxes;
  }

  private renderMarkers() {
    this.clearMarkers();

    const locations = this.submodelLocations$.value;
    const markersData = locations.map(loc => ({
      position: loc.Centroid,
      label: loc.Name
    }));

    this.labelInfos = createSimpleGeometryCenterLabels2DObjects(
      this.submodelsMarkersGroup as any,
      markersData as any
    );

    this.labelInfos.forEach((info, index) => {
      const element = info.object2d.element as HTMLElement;
      element.style.pointerEvents = 'auto';
      element.style.cursor = 'pointer';
      element.onclick = (e) => {
        e.stopPropagation();
        this.selectSubmodel(locations[index].AssetId);
      };
      element.addEventListener('pointerdown', (e) => e.stopPropagation());
      element.addEventListener('mousedown', (e) => e.stopPropagation());
    });
  }

  private highlightBoundingBox(assetId: string) {
    this.clearBoundingBoxes();
    const loc = this.submodelLocations$.value.find(l => l.AssetId === assetId);
    if (!loc) return;

    const boxHelper = new THREE.Box3Helper(loc.AABB, new THREE.Color(0xffff00));
    this.submodelsBoxGroup.add(boxHelper);
  }

  public async navigateToSubmodel(assetId: string) {
    try {
      this.phdsoftCommonService.stopPromises();

      let currentAsset = await this.assetService.getCurrentAsset();
      const submodelData = await this.assetService.getFromMicroservice(assetId);
      const assetInfoModel = submodelData?.AssetInfo;

      if (!assetInfoModel) return;

      this.applyRegionMappingContext(currentAsset, assetInfoModel);

      currentAsset.AssetId = assetId;
      currentAsset.AssetInfo = assetInfoModel;
      await this.assetService.saveCurrentAsset(currentAsset);
      window.location.reload();
    } catch (error) {
      console.error('Failed to navigate to submodel:', error);
    }
  }

  private applyRegionMappingContext(globalAsset: any, submodelAssetInfo: any) {
    if (!submodelAssetInfo.Regions) return;

    const activeGlobalRegions = this.getActiveGlobalRegions(globalAsset);

    if (activeGlobalRegions.length > 0) {
      const matchedCount = this.mapAndSelectIntersectingRegions(activeGlobalRegions, submodelAssetInfo.Regions);

      if (matchedCount === 0) {
        this.fallbackSelectAllRegions(submodelAssetInfo.Regions);
      }
    } else {
      this.fallbackSelectAllRegions(submodelAssetInfo.Regions);
    }
  }

  private getActiveGlobalRegions(globalAsset: any): any[] {
    const globalRegions: any[] = globalAsset?.AssetInfo?.Regions || [];
    return globalRegions.filter((r: any) => r.selected);
  }

  private mapAndSelectIntersectingRegions(activeGlobalRegions: any[], submodelRegions: any[]): number {
    let matchedCount = 0;

    const activeIds = new Set(activeGlobalRegions.map((r: any) => r.id).filter(Boolean));

    submodelRegions.forEach((region: any) => {
      const regionId = region.id;

      const isMatch = regionId && activeIds.has(regionId);

      region.selected = !!isMatch;
      if (region.selected) {
        matchedCount++;
      }
    });

    return matchedCount;
  }

  private fallbackSelectAllRegions(regions: any[]) {
    regions.forEach((region: any) => {
      region.selected = true;
    });
  }

  private clearMarkers() {
    disposeGroupLinesAndLabels(this.submodelsMarkersGroup as any);
    this.labelInfos = [];
  }

  private clearBoundingBoxes() {
    while (this.submodelsBoxGroup.children.length > 0) {
      const child = this.submodelsBoxGroup.children[0];
      this.submodelsBoxGroup.remove(child);
    }
  }

  private clearGraphics() {
    this.clearMarkers();
    this.clearBoundingBoxes();

    if (this.assetManager.graphics?.scene) {
      // TODO: remove 'as unknown as any' casts when production migrates to new THREE version
      this.assetManager.graphics.scene.remove(this.submodelsMarkersGroup as unknown as any);
      this.assetManager.graphics.scene.remove(this.submodelsBoxGroup as unknown as any);
    }
  }
}